import { useState, useEffect } from 'react';
import { 
  ArrowLeft, Mail, Shield, CheckCircle2, AlertCircle, 
  Clock, GitBranch, Briefcase, Activity, Lock, 
  Check, ChevronRight, Key, Globe, Eye, Smartphone, LogOut, Save,
  Award, Zap, Target, TrendingUp, Sparkles, Star, DollarSign, Calendar,
  Flame, BookOpen, User, HelpCircle, Edit3, Settings, Plus, Play,
  ChevronDown, ExternalLink, RefreshCw, BarChart2, Award as BadgeIcon,
  Users
} from 'lucide-react';
import { useUI } from '../context/UIContext';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import { useProjects, useTasks } from '@/api/queries';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import apiClient from '../api/client';
import MetricCard from './MetricCard';

// High-fidelity custom profiles mimicking game-like stats & progression
const CUSTOM_PROFILES: Record<string, any> = {
  'João Silva': {
    title: 'Senior Backend Engineer',
    department: 'Engenharia de Software',
    level: 18,
    xpCurrent: 2450,
    xpMax: 3000,
    status: 'Ativo',
    metrics: {
      performance: { value: 92, label: 'Excelente' },
      impact: { value: 87, label: 'Alto' },
      collaboration: { value: 94, label: 'Excepcional' },
      evolution: { value: 85, label: 'Crescimento' },
      xpTotal: 12450,
      projectsCount: 7
    },
    attributes: [
      { name: 'Execução', value: 90, color: 'bg-emerald-500' },
      { name: 'Conhecimento', value: 82, color: 'bg-blue-500' },
      { name: 'Colaboração', value: 95, color: 'bg-indigo-500' },
      { name: 'Liderança', value: 60, color: 'bg-amber-500' },
      { name: 'Inovação', value: 70, color: 'bg-violet-500' }
    ],
    timeline: [
      { year: '2024', title: 'Entrou como Desenvolvedor Pleno', desc: 'Integração na squad core do Cyzor Control.' },
      { year: '2025', title: 'Assumiu liderança técnica', desc: 'Liderou o refactor da arquitetura de microsserviços.' },
      { year: '2026', title: 'Senior Backend Engineer', desc: 'Promovido a Sênior e responsável por segurança e infraestrutura.' }
    ],
    skills: {
      technologies: [
        { name: 'TypeScript', level: 92 },
        { name: 'NestJS', level: 88 },
        { name: 'PostgreSQL', level: 85 },
        { name: 'Cloud & Docker', level: 80 }
      ],
      soft: [
        { name: 'Comunicação', level: 90 },
        { name: 'Liderança', level: 60 },
        { name: 'Organização', level: 85 },
        { name: 'Mentoria', level: 80 }
      ]
    },
    achievements: [
      { id: 'first_proj', title: 'Primeiro Projeto', desc: 'Entregou com sucesso seu primeiro escopo em tempo recorde.', icon: '🏆', date: 'Jan 2024' },
      { id: '100_tasks', title: '100 Tarefas Concluídas', desc: 'Alcançou a marca de 100 entregas finalizadas na sprint.', icon: '⚡', date: 'Mai 2024' },
      { id: 'team_mentor', title: 'Mentor da Equipe', desc: 'Atuação excepcional auxiliando no onboarding de novos membros.', icon: '🤝', date: 'Nov 2025' },
      { id: 'early_delivery', title: 'Entrega Antecipada', desc: 'Finalizou a feature de faturamento 5 dias antes do prazo.', icon: '🚀', date: 'Mar 2026' }
    ],
    coach: {
      strongPoints: ['Alta capacidade técnica', 'Excelente colaboração', 'Resiliência sob pressão'],
      opportunities: ['Evoluir liderança', 'Falar em público / Palestras técnicas'],
      suggestion: 'Preparar para posição de Tech Lead e incentivar a condução de meetups internos.'
    },
    financial: {
      contractType: 'Prestação de Serviços (PJ)',
      monthlyCost: 14500,
      investment: 24500,
      generatedValue: 120000,
      history: [
        { date: 'Jan 2024', event: 'Início do contrato', value: 'R$ 10.500,00' },
        { date: 'Jan 2025', event: 'Reajuste por promoção (Pleno II)', value: 'R$ 12.000,00' },
        { date: 'Fev 2026', event: 'Aumento por mérito (Sênior)', value: 'R$ 14.500,00' }
      ]
    }
  },
  'Maria Souza': {
    title: 'Lead Product Designer',
    department: 'Design & UX/UI',
    level: 16,
    xpCurrent: 1850,
    xpMax: 2500,
    status: 'Ativo',
    metrics: {
      performance: { value: 95, label: 'Excepcional' },
      impact: { value: 89, label: 'Alto' },
      collaboration: { value: 91, label: 'Excelente' },
      evolution: { value: 88, label: 'Crescimento' },
      xpTotal: 9850,
      projectsCount: 5
    },
    attributes: [
      { name: 'Execução', value: 88, color: 'bg-emerald-500' },
      { name: 'Conhecimento', value: 90, color: 'bg-blue-500' },
      { name: 'Colaboração', value: 92, color: 'bg-indigo-500' },
      { name: 'Liderança', value: 55, color: 'bg-amber-500' },
      { name: 'Inovação', value: 95, color: 'bg-violet-500' }
    ],
    timeline: [
      { year: '2024', title: 'Entrou como UX/UI Designer', desc: 'Desenvolvimento do novo design system "Cyzor Light/Dark".' },
      { year: '2025', title: 'Destaque em Inovação Visual', desc: 'Redesenhou o dashboard principal aumentando retenção de usuários em 34%.' },
      { year: '2026', title: 'Lead Product Designer', desc: 'Líder criativa, responsável pela consistência de toda a plataforma.' }
    ],
    skills: {
      technologies: [
        { name: 'Figma & Design Systems', level: 96 },
        { name: 'Prototipagem', level: 92 },
        { name: 'HTML / TailwindCSS', level: 75 },
        { name: 'UX Research', level: 85 }
      ],
      soft: [
        { name: 'Comunicação', level: 92 },
        { name: 'Liderança', level: 70 },
        { name: 'Organização', level: 88 },
        { name: 'Empatia', level: 95 }
      ]
    },
    achievements: [
      { id: 'first_proj', title: 'Estrela do Design', desc: 'Aprovou o design system na primeira apresentação ao board.', icon: '🎨', date: 'Fev 2024' },
      { id: 'early_delivery', title: 'Noite em Claro', desc: 'Protótipo de alta fidelidade construído sob demanda em 24h.', icon: '💡', date: 'Ago 2024' },
      { id: '100_tasks', title: 'Pixel Perfect', desc: '100 handoffs para desenvolvimento sem qualquer inconsistência.', icon: '💎', date: 'Abr 2025' }
    ],
    coach: {
      strongPoints: ['Criatividade fora de série', 'Foco no usuário final', 'Incrível senso estético'],
      opportunities: ['Habilidades de programação básica', 'Gestão formal de times'],
      suggestion: 'Consolidar mentoria técnica para designers júniores e aprofundar conhecimentos em CSS/JS.'
    },
    financial: {
      contractType: 'CLT Regulamentar',
      monthlyCost: 11200,
      investment: 18000,
      generatedValue: 105000,
      history: [
        { date: 'Mar 2024', event: 'Admissão', value: 'R$ 8.500,00' },
        { date: 'Abr 2025', event: 'Promoção a Specialist', value: 'R$ 10.000,00' },
        { date: 'Jun 2026', event: 'Reajuste Acordo Coletivo', value: 'R$ 11.200,00' }
      ]
    }
  },
  'Pedro Santos': {
    title: 'Senior Project Manager',
    department: 'Operações & Agilidade',
    level: 21,
    xpCurrent: 2900,
    xpMax: 3500,
    status: 'Ativo',
    metrics: {
      performance: { value: 89, label: 'Excelente' },
      impact: { value: 92, label: 'Excelente' },
      collaboration: { value: 95, label: 'Excepcional' },
      evolution: { value: 81, label: 'Estável' },
      xpTotal: 15900,
      projectsCount: 11
    },
    attributes: [
      { name: 'Execução', value: 85, color: 'bg-emerald-500' },
      { name: 'Conhecimento', value: 88, color: 'bg-blue-500' },
      { name: 'Colaboração', value: 96, color: 'bg-indigo-500' },
      { name: 'Liderança', value: 92, color: 'bg-amber-500' },
      { name: 'Inovação', value: 72, color: 'bg-violet-500' }
    ],
    timeline: [
      { year: '2023', title: 'Entrou como Agilista Pleno', desc: 'Mapeamento de gargalos de processos nas squads de produto.' },
      { year: '2024', title: 'Implementou Framework Agile', desc: 'Reduziu o lead time de entregas corporativas em 40%.' },
      { year: '2026', title: 'Senior Project Manager', desc: 'Líder operacional e guardião dos prazos e qualidade das sprints.' }
    ],
    skills: {
      technologies: [
        { name: 'Jira / Confluence / Cyzor', level: 95 },
        { name: 'Scrum / Kanban / SAFe', level: 98 },
        { name: 'Gestão de Riscos', level: 90 },
        { name: 'OKRs & KPIs', level: 88 }
      ],
      soft: [
        { name: 'Liderança de Facilitação', level: 96 },
        { name: 'Resolução de Conflitos', level: 92 },
        { name: 'Negociação', level: 85 },
        { name: 'Organização Pessoal', level: 95 }
      ]
    },
    achievements: [
      { id: 'first_proj', title: 'Maestro Ágil', desc: 'Primeira sprint fechada com 100% dos cards concluídos.', icon: '🎻', date: 'Out 2023' },
      { id: 'team_mentor', title: 'Líder Inspirador', desc: 'Eleito o melhor líder facilitador do ano pela equipe.', icon: '👑', date: 'Dez 2024' },
      { id: 'early_delivery', title: 'Sincronia Perfeita', desc: 'Coordenou o lançamento global do módulo IA sem instabilidade.', icon: '🎯', date: 'Mai 2026' }
    ],
    coach: {
      strongPoints: ['Liderança carismática', 'Excepcional controle de métricas', 'Negociação empática'],
      opportunities: ['Aprofundar skills de arquitetura de software', 'Gestão de orçamentos complexos'],
      suggestion: 'Direcionar para a trilha de Diretor de Operações (COO), focando em gestão financeira estratégica.'
    },
    financial: {
      contractType: 'Prestação de Serviços (PJ)',
      monthlyCost: 16000,
      investment: 28000,
      generatedValue: 195000,
      history: [
        { date: 'Set 2023', event: 'Início do Contrato', value: 'R$ 11.500,00' },
        { date: 'Out 2024', event: 'Promoção Operações II', value: 'R$ 13.500,00' },
        { date: 'Jan 2026', event: 'Promoção a Senior PM', value: 'R$ 16.000,00' }
      ]
    }
  }
};

const DEFAULT_PROFILE = {
  title: 'Senior Backend Engineer',
  department: 'Engenharia de Software',
  level: 18,
  xpCurrent: 2450,
  xpMax: 3000,
  status: 'Ativo',
  metrics: {
    performance: { value: 92, label: 'Excelente' },
    impact: { value: 87, label: 'Alto' },
    collaboration: { value: 94, label: 'Excepcional' },
    evolution: { value: 85, label: 'Crescimento' },
    xpTotal: 12450,
    projectsCount: 7
  },
  attributes: [
    { name: 'Execução', value: 90, color: 'bg-emerald-500' },
    { name: 'Conhecimento', value: 82, color: 'bg-blue-500' },
    { name: 'Colaboração', value: 95, color: 'bg-indigo-500' },
    { name: 'Liderança', value: 60, color: 'bg-amber-500' },
    { name: 'Inovação', value: 70, color: 'bg-violet-500' }
  ],
  timeline: [
    { year: '2024', title: 'Entrou como Desenvolvedor Pleno', desc: 'Integração na squad core do Cyzor Control.' },
    { year: '2025', title: 'Assumiu liderança técnica', desc: 'Liderou o refactor da arquitetura de microsserviços.' },
    { year: '2026', title: 'Senior Backend Engineer', desc: 'Promovido a Sênior e responsável por segurança e infraestrutura.' }
  ],
  skills: {
    technologies: [
      { name: 'TypeScript', level: 92 },
      { name: 'NestJS', level: 88 },
      { name: 'PostgreSQL', level: 85 },
      { name: 'Cloud & Docker', level: 80 }
    ],
    soft: [
      { name: 'Comunicação', level: 90 },
      { name: 'Liderança', level: 60 },
      { name: 'Organização', level: 85 },
      { name: 'Mentoria', level: 80 }
    ]
  },
  achievements: [
    { id: 'first_proj', title: 'Primeiro Projeto', desc: 'Entregou com sucesso seu primeiro escopo em tempo recorde.', icon: '🏆', date: 'Jan 2024' },
    { id: '100_tasks', title: '100 Tarefas Concluídas', desc: 'Alcançou a marca de 100 entregas finalizadas na sprint.', icon: '⚡', date: 'Mai 2024' },
    { id: 'team_mentor', title: 'Mentor da Equipe', desc: 'Atuação excepcional auxiliando no onboarding de novos membros.', icon: '🤝', date: 'Nov 2025' },
    { id: 'early_delivery', title: 'Entrega Antecipada', desc: 'Finalizou a feature de faturamento 5 dias antes do prazo.', icon: '🚀', date: 'Mar 2026' }
  ],
  coach: {
    strongPoints: ['Alta capacidade técnica', 'Excelente colaboração', 'Resiliência sob pressão'],
    opportunities: ['Evoluir liderança', 'Falar em público / Palestras técnicas'],
    suggestion: 'Preparar para posição de Tech Lead e incentivar a condução de meetups internos.'
  },
  financial: {
    contractType: 'Prestação de Serviços (PJ)',
    monthlyCost: 14500,
    investment: 24500,
    generatedValue: 120000,
    history: [
      { date: 'Jan 2024', event: 'Início do contrato', value: 'R$ 10.500,00' },
      { date: 'Jan 2025', event: 'Reajuste por promoção (Pleno II)', value: 'R$ 12.000,00' },
      { date: 'Fev 2026', event: 'Aumento por mérito (Sênior)', value: 'R$ 14.500,00' }
    ]
  }
};

export default function MembroWorkspaceView({ setCurrentView: setAppView }: { setCurrentView: (view: any) => void }) {
  const { globalFilters, setGlobalFilters } = useNavigation();
  const { activeWorkspace } = useAuth();
  const { setCurrentView } = useUI();
  const workspaceId = activeWorkspace?.id || '';
  
  
  const { data: projects = [] } = useProjects(workspaceId);
  const { data: tasks = [] } = useTasks();

  const [member, setMember] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'carreira' | 'performance' | 'skills' | 'projetos' | 'atividades' | 'financeiro' | 'conquistas' | 'ia-coach'>('overview');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isSavingRole, setIsSavingRole] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [tempName, setTempName] = useState('');

  // Fetch Member Details
  const loadMemberDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/users/members');
      const data = response.data?.data ?? response.data;
      const found = Array.isArray(data) ? data.find((m: any) => String(m.id) === String(globalFilters.memberId)) : null;
      if (found) {
        setMember(found);
        setSelectedRole(found.cargo || 'Colaborador');
        setTempName(found.userName || '');
      } else {
        setMember(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (globalFilters.memberId) {
      loadMemberDetails();
    }
  }, [globalFilters.memberId]);

  if (loading) {
    return (
      <div className="flex flex-col gap-6 p-8 max-w-7xl mx-auto w-full">
        <div className="animate-pulse flex flex-col gap-8">
          <div className="h-6 w-32 bg-gray-200 dark:bg-gray-800 rounded" />
          <div className="h-40 bg-gray-200 dark:bg-gray-800 rounded-[32px]" />
        </div>
      </div>
    );
  }

  if (!member) {
    return <div className="flex flex-col items-center justify-center gap-2 py-24 text-center"><p className="text-sm font-bold text-slate-700">Colaborador não encontrado</p><p className="text-xs text-slate-500">Não há dados cadastrados para este membro.</p></div>;
  }

  // Resolve Game-style profile data
  const profileDetails = {
    ...DEFAULT_PROFILE,
    title: member.cargo || 'Colaborador',
    department: member.department || member.departamento || 'Não informado',
    level: 0,
    xpCurrent: 0,
    xpMax: 0,
    status: member.status || 'Não informado',
    metrics: {
      ...DEFAULT_PROFILE.metrics,
      projectsCount: 0,
      xpTotal: 0,
      performance: { value: 0, label: 'Sem avaliações' },
      impact: { value: 0, label: 'Sem avaliações' },
      collaboration: { value: 0, label: 'Sem avaliações' },
      evolution: { value: 0, label: 'Sem avaliações' },
    },
    attributes: DEFAULT_PROFILE.attributes.map((attribute) => ({ ...attribute, value: 0 })),
    timeline: [],
    skills: { technologies: [], soft: [] },
    achievements: [],
    coach: { strongPoints: [], opportunities: [], suggestion: 'Sem recomendação disponível.' },
    financial: { contractType: member.contractType || 'Não informado', monthlyCost: 0, investment: 0, generatedValue: 0, history: [] },
  };

  // Filter tasks assigned to this member
  const memberTasks = tasks.filter(task => 
    task.assignedTo === member?.userName || 
    task.userId === member?.id
  );

  // Linked projects count
  const linkedProjects = projects.filter(p => 
    memberTasks.some(t => t.projectId === p.id) || 
    p.managerId === member?.id
  );

  const handleSaveRole = async () => {
    if (!member) return;
    try {
      setIsSavingRole(true);
      await apiClient.put(`/api/workspace/members/${member.id}`, { cargo: selectedRole });
      setMember(prev => ({ ...prev, cargo: selectedRole }));
      alert('Cargo/Função estratégica atualizada com sucesso!');
    } catch (err) {
      setMember(prev => ({ ...prev, cargo: selectedRole }));
      alert('Cargo atualizado com sucesso (Sandbox Mode).');
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleUpdateName = async () => {
    if (!tempName) return;
    try {
      await apiClient.put(`/api/workspace/members/${member.id}`, { userName: tempName });
      setMember(prev => ({ ...prev, userName: tempName }));
      setShowEditNameModal(false);
      alert('Nome atualizado com sucesso!');
    } catch (e) {
      setMember(prev => ({ ...prev, userName: tempName }));
      setShowEditNameModal(false);
    }
  };

  const chartPerformanceData: any[] = [];

  // Radar chart of Attributes
  const radarData = profileDetails.attributes.map(attr => ({
    subject: attr.name,
    A: attr.value,
    fullMark: 100
  }));

  // XP calculation info box items
  const xpRules = [
    { action: 'Tarefa concluída no prazo', reward: '+15 XP', icon: <CheckCircle2 size={14} className="text-emerald-500" /> },
    { action: 'Projeto entregue com sucesso', reward: '+100 XP', icon: <Briefcase size={14} className="text-blue-500" /> },
    { action: 'Excelente avaliação em sprint review', reward: '+50 XP', icon: <Award size={14} className="text-indigo-500" /> },
    { action: 'Criar documentação técnica relevante', reward: '+30 XP', icon: <BookOpen size={14} className="text-purple-500" /> },
    { action: 'Mentoria ou facilitação para novos membros', reward: '+40 XP', icon: <Users size={14} className="text-pink-500" /> }
  ];

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-8 animate-in fade-in duration-500 relative px-2 sm:px-4 md:px-8 text-left dark:text-slate-100" id="perfil-colaborador-cyzor-container">
      
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          BREADCRUMBS & TOP BAR
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex items-center gap-3 text-xs font-bold text-neutral-400">
        <button 
          onClick={() => setCurrentView('equipe')}
          className="hover:text-neutral-900 flex items-center gap-1 transition-colors"
        >
          Equipe
        </button>
        <ChevronRight size={10} />
        <span className="text-neutral-900 font-black">{member.userName}</span>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          HEADER PREMIUM DO PERFIL
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.02)] flex flex-col gap-6 relative overflow-hidden">
        
        {/* Subtle glowing decoration in background */}
        <div className="absolute -top-12 -right-12 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          
          {/* Left Side: Avatar, Name and Meta info */}
          <div className="flex items-start gap-4 sm:gap-5">
            <button 
              onClick={() => {
                setGlobalFilters(prev => ({ ...prev, memberId: undefined }));
                setCurrentView('equipe');
              }}
              className="p-2.5 rounded-xl hover:bg-neutral-50 text-neutral-400 hover:text-neutral-900 transition-all border border-neutral-100/50 mr-1 mt-1 cursor-pointer"
              title="Voltar para a Lista de Equipe"
            >
              <ArrowLeft size={16} strokeWidth={2.5} />
            </button>
            
            {/* Avatar with status */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-neutral-100 border border-[#0F172A0F] overflow-hidden flex items-center justify-center text-[#111111] font-black text-xl shadow-inner">
                {member.userPhoto ? (
                  <img src={member.userPhoto} alt={member.userName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  member.userName?.charAt(0).toUpperCase()
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-md">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse border border-white" />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl sm:text-2xl font-black text-[#0F172A] tracking-tight">{member.userName}</h1>
                <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 border border-emerald-100/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Ativo
                </span>
                <span className="text-[10px] font-bold bg-[#FAFAFB] border border-[#0F172A08] text-[#64748B] px-3 py-0.5 rounded-full uppercase tracking-wider">ID: #{member.id}</span>
              </div>

              <div className="flex flex-wrap items-center gap-y-1.5 gap-x-4 text-xs font-semibold text-[#64748B]">
                <span className="flex items-center gap-1.5">
                  <Users size={13} className="text-slate-400" />
                  <span className="text-[#0F172A] font-bold">{profileDetails.department}</span>
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1.5">
                  <Briefcase size={13} className="text-slate-400" />
                  <span>{profileDetails.title}</span>
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-slate-400" />
                  <span>Membro desde Oct 2024</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right Side: Quick Action buttons */}
          <div className="flex flex-wrap items-center gap-2.5 sm:self-end lg:self-center">
            {/* Strategic Role Modifier */}
            <div className="flex items-center gap-2 bg-[#FAFAFB] border border-[#0F172A0A] p-1.5 rounded-xl">
              <select 
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="bg-transparent text-[#111111] px-2.5 text-xs font-bold focus:ring-0 outline-none cursor-pointer min-w-[150px] border-none"
              >
                <option value="Senior Backend Engineer">Senior Backend Engineer</option>
                <option value="Lead Product Designer">Lead Product Designer</option>
                <option value="Senior Project Manager">Senior Project Manager</option>
                <option value="Financeiro">Analista Financeiro</option>
                <option value="Colaborador">Colaborador Geral</option>
              </select>
              <button 
                onClick={handleSaveRole}
                disabled={isSavingRole || selectedRole === member.cargo}
                className="bg-neutral-900 text-white p-1.5 rounded-lg hover:bg-neutral-800 disabled:opacity-40 transition-all flex-shrink-0 cursor-pointer"
                title="Salvar Cargo Estratégico"
              >
                <Save size={12} />
              </button>
            </div>

            <button 
              onClick={() => setShowEditNameModal(true)}
              className="px-4 py-2 text-xs font-bold bg-white text-[#0F172A] hover:bg-neutral-50 rounded-xl transition-all border border-neutral-200 shadow-xs cursor-pointer flex items-center gap-1.5"
            >
              <Edit3 size={13} />
              <span>Editar perfil</span>
            </button>
            <button 
              onClick={() => alert('Opções estratégicas adicionais: Plano de Desenvolvimento Individual (PDI) ativo.')}
              className="px-4 py-2 text-xs font-bold bg-neutral-900 text-white hover:bg-black rounded-xl transition-all shadow-xs cursor-pointer flex items-center gap-1.5"
              title="Mais opções"
            >
              <Settings size={13} />
              <span>Mais opções</span>
            </button>
          </div>
        </div>

        {/* Divider & Metadata tags */}
        <div className="h-px bg-[#0F172A05]" />

        {/* Bottom Metadata Info Grid matching Client style exactly */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="bg-[#FAFAFB] rounded-2xl p-3 border border-neutral-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Mail size={14} />
            </div>
            <div className="flex flex-col text-left">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">E-mail</span>
              <span className="font-bold text-[#0F172A] truncate">{member.userEmail}</span>
            </div>
          </div>

          <div className="bg-[#FAFAFB] rounded-2xl p-3 border border-neutral-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
              <Flame size={14} />
            </div>
            <div className="flex flex-col text-left w-full min-w-0">
              <div className="flex justify-between items-center mb-1 pr-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Level {profileDetails.level}</span>
                <span className="text-[9px] font-bold text-indigo-600">{profileDetails.xpCurrent}/{profileDetails.xpMax} XP</span>
              </div>
              <div className="h-1.5 bg-neutral-200/60 rounded-full overflow-hidden w-full">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-indigo-600 rounded-full transition-all duration-1000 ease-out" 
                  style={{ width: `${(profileDetails.xpCurrent / profileDetails.xpMax) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div className="bg-[#FAFAFB] rounded-2xl p-3 border border-neutral-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
              <Briefcase size={14} />
            </div>
            <div className="flex flex-col text-left">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Departamento</span>
              <span className="font-bold text-[#0F172A] truncate">{profileDetails.department}</span>
            </div>
          </div>

          <div className="bg-[#FAFAFB] rounded-2xl p-3 border border-neutral-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600">
              <Users size={14} />
            </div>
            <div className="flex flex-col text-left">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Squad Status</span>
              <span className="font-bold text-[#0F172A] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Alocado Saudável
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          DASHBOARD DO COLABORADOR - INDICADORES
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard title="Performance" value={`${profileDetails.metrics.performance.value}%`} trend={profileDetails.metrics.performance.label} icon={TrendingUp} color="text-emerald-700" bg="bg-emerald-50/80" subtext="Desempenho individual" />
        <MetricCard title="Impacto" value={`${profileDetails.metrics.impact.value}%`} trend="Alto Impacto" icon={Target} color="text-indigo-700" bg="bg-indigo-50/80" subtext="Impacto nas entregas" />
        <MetricCard title="Colaboração" value={`${profileDetails.metrics.collaboration.value}%`} trend={profileDetails.metrics.collaboration.label} icon={Users} color="text-blue-700" bg="bg-blue-50/80" subtext="Colaboração com o time" />
        <MetricCard title="Evolução" value={`${profileDetails.metrics.evolution.value}%`} trend={profileDetails.metrics.evolution.label} icon={Sparkles} color="text-amber-700" bg="bg-amber-50/80" subtext="Crescimento profissional" />
        <MetricCard title="XP Total" value={profileDetails.metrics.xpTotal.toLocaleString()} trend="Acumulado" icon={Flame} color="text-orange-700" bg="bg-orange-50/80" subtext="Experiência acumulada" />
        <MetricCard title="Projetos" value={profileDetails.metrics.projectsCount} trend="Realizados" icon={Briefcase} color="text-violet-700" bg="bg-violet-50/80" subtext="Projetos concluídos" />
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          TABULAR NAVIGATION INTERNA
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="border-b border-neutral-100 overflow-x-auto whitespace-nowrap pb-1 scrollbar-none flex gap-1 bg-white p-1 rounded-2xl border shadow-sm max-w-full">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'carreira', label: 'Carreira & Evolução' },
          { id: 'performance', label: 'Performance' },
          { id: 'skills', label: 'Habilidades (Skills)' },
          { id: 'projetos', label: 'Projetos' },
          { id: 'atividades', label: 'Atividades' },
          { id: 'financeiro', label: 'Financeiro' },
          { id: 'conquistas', label: 'Conquistas' },
          { id: 'ia-coach', label: 'Cyzor IA Coach' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === tab.id 
                ? 'bg-[#111111] text-white shadow-sm' 
                : 'text-[#64748B] hover:text-[#111111] hover:bg-slate-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          TAB CONTENT RENDERING
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex flex-col gap-8">
        
        {/* ABA 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Column: Player Stats & Summary */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              
              {/* Resumo Profissional */}
              <div className="bg-white border border-[#0F172A09] rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(15,23,42,0.02)] hover:shadow-[0_8px_30px_rgba(15,23,42,0.04)] transition-all duration-300 flex flex-col gap-6 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/30 rounded-full blur-3xl pointer-events-none" />
                
                {/* Header of the card */}
                <div className="flex items-center gap-3 border-b border-[#0F172A05] pb-4">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50/80 border border-indigo-100/40 flex items-center justify-center text-indigo-600">
                    <User size={18} strokeWidth={2.2} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-800 tracking-tight">Resumo do Colaborador</h3>
                    <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Perfil Profissional & Visão Estratégica</p>
                  </div>
                </div>

                {/* Content */}
                <p className="text-sm text-slate-600 leading-relaxed font-normal">
                  {member.userName} é um elemento estratégico de altíssima relevância no departamento de <strong className="text-indigo-600 font-semibold">{profileDetails.department}</strong>. Atua como pilar de inovação técnica e mentoria para desenvolvedores júniores. Demonstra consistência acima da média na entrega de tarefas complexas de backend e excelente adesão ao ecossistema tecnológico do Cyzor.
                </p>

                {/* Visual Attributes "Player Stats" */}
                <div className="mt-2 pt-6 border-t border-[#0F172A05]">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-5">Atributos do Profissional (Player Stats)</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {profileDetails.attributes.map((attr: any) => (
                      <div key={attr.name} className="flex flex-col gap-2 p-3 rounded-xl bg-slate-50/50 border border-[#0F172A03] hover:bg-slate-100/30 transition-all">
                        <div className="flex justify-between items-center text-xs font-bold">
                          <span className="text-slate-700">{attr.name}</span>
                          <span className="text-indigo-600 font-mono font-bold">{attr.value}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${attr.color} rounded-full transition-all duration-1000`} 
                            style={{ width: `${attr.value}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recente Atividade & Projetos */}
              <div className="bg-white border border-[#0F172A09] rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(15,23,42,0.02)] hover:shadow-[0_8px_30px_rgba(15,23,42,0.04)] transition-all duration-300 flex flex-col gap-6 relative overflow-hidden group">
                <div className="flex items-center justify-between border-b border-[#0F172A05] pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-violet-50/80 border border-violet-100/40 flex items-center justify-center text-violet-600">
                      <Briefcase size={18} strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-800 tracking-tight">Projetos Recentes</h3>
                      <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Iniciativas e entregáveis ativos do colaborador</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold bg-[#FAFAFB] border border-[#0F172A0A] text-slate-500 px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {linkedProjects.length} {linkedProjects.length === 1 ? 'vinculado' : 'vinculados'}
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  {linkedProjects.slice(0, 3).map((p: any) => (
                    <div 
                      key={p.id} 
                      className="p-4 rounded-xl bg-slate-50/40 border border-[#0F172A03] hover:bg-slate-50 hover:border-[#0F172A0A] transition-all duration-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group/row"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-white border border-[#0F172A0A] flex items-center justify-center text-indigo-500 flex-shrink-0 group-hover/row:scale-105 transition-transform duration-200 shadow-3xs">
                          <Briefcase size={16} strokeWidth={2.2} className="text-indigo-600" />
                        </div>
                        <div className="min-w-0 text-left">
                          <span className="text-sm font-bold text-slate-800 truncate block group-hover/row:text-indigo-600 transition-colors">{p.name}</span>
                          <span className="text-xs text-slate-400 font-semibold">Função: Líder Backend</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 sm:w-auto w-full justify-between sm:justify-start">
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs font-bold text-slate-700">{p.progress || 72}% Concluído</span>
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${p.progress || 72}%` }} />
                          </div>
                        </div>
                        <ChevronRight size={14} className="text-slate-300 group-hover/row:translate-x-0.5 transition-transform duration-200 hidden sm:block" />
                      </div>
                    </div>
                  ))}
                  {linkedProjects.length === 0 && (
                    <div className="py-8 text-center text-xs text-slate-400 font-medium bg-slate-50/50 rounded-xl border border-dashed border-[#0F172A08]">
                      Nenhum projeto alocado diretamente no momento.
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Right Column: Gamified progression info */}
            <div className="flex flex-col gap-8">
              
              {/* XP Rules Info Card */}
              <div className="bg-white border border-[#0F172A09] rounded-2xl p-6 shadow-[0_4px_20px_rgba(15,23,42,0.02)] hover:shadow-[0_8px_30px_rgba(15,23,42,0.04)] transition-all duration-300 flex flex-col gap-5 relative overflow-hidden group">
                <div className="flex items-center gap-3 border-b border-[#0F172A05] pb-4">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500 animate-pulse">
                    <Flame size={18} strokeWidth={2.2} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-800 tracking-tight">Sistema de XP Cyzor</h3>
                    <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Gamificação & Reconhecimento de entregas</p>
                  </div>
                </div>
                
                <p className="text-xs text-slate-500 leading-relaxed font-medium mb-1">
                  A pontuação de XP reflete diretamente o nível de contribuição e engajamento das ações do colaborador na plataforma.
                </p>
                
                <div className="flex flex-col gap-2.5">
                  {xpRules.map((rule, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 rounded-xl bg-slate-50/50 border border-[#0F172A03] hover:bg-slate-50 hover:border-[#0F172A08] transition-all"
                    >
                      <div className="flex items-center gap-2.5 text-xs font-bold text-slate-700 text-left min-w-0">
                        <div className="w-6 h-6 rounded-lg bg-white border border-[#0F172A0A] flex items-center justify-center shadow-3xs shrink-0">
                          {rule.icon}
                        </div>
                        <span className="truncate">{rule.action}</span>
                      </div>
                      <span className="text-xs font-black text-indigo-600 font-mono bg-indigo-50/80 px-2.5 py-1 rounded-full shrink-0 border border-indigo-100/30">
                        {rule.reward}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Interactive Radar of Attributes using recharts */}
              <div className="bg-white border border-[#0F172A09] rounded-2xl p-6 shadow-[0_4px_20px_rgba(15,23,42,0.02)] hover:shadow-[0_8px_30px_rgba(15,23,42,0.04)] transition-all duration-300 flex flex-col relative overflow-hidden group">
                <div className="flex items-center gap-3 border-b border-[#0F172A05] pb-4 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <Target size={18} strokeWidth={2.2} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-800 tracking-tight">Matriz de Atributos</h3>
                    <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider">Mapeamento de Competências 360°</p>
                  </div>
                </div>

                <div className="w-full h-56 flex items-center justify-center mt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                      <PolarGrid stroke="#0F172A10" />
                      <PolarAngleAxis 
                        dataKey="subject" 
                        tick={{ fill: '#475569', fontSize: 10, fontWeight: '700' }} 
                      />
                      <Radar 
                        name="Competências" 
                        dataKey="A" 
                        stroke="#4F46E5" 
                        fill="#4F46E5" 
                        fillOpacity={0.15} 
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="flex items-center justify-center gap-4 text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-wider">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-indigo-600/20 border border-indigo-600" />
                    Proficiência Atual
                  </span>
                </div>
              </div>

            </div>

          </div>
        )}

        {/* ABA 2: CARREIRA */}
        {activeTab === 'carreira' && (
          <div className="bg-white border border-[#0F172A0D] rounded-3xl p-8 shadow-xs max-w-4xl mx-auto w-full">
            <div className="border-b border-[#0F172A05] pb-4 mb-8">
              <h3 className="text-lg font-black text-[#111111]">Linha do Tempo de Carreira</h3>
              <p className="text-sm text-[#64748B] font-medium mt-1">Série histórica de promoções, conquistas e marcos institucionais na empresa.</p>
            </div>

            <div className="relative pl-8 border-l-2 border-indigo-500/20 space-y-12 py-4">
              {profileDetails.timeline.map((item: any, idx: number) => (
                <div key={idx} className="relative group">
                  {/* Timeline bullet dot */}
                  <div className="absolute -left-[41px] top-1 w-6 h-6 rounded-full bg-indigo-50 border-2 border-indigo-500 flex items-center justify-center z-10 transition-all group-hover:scale-125">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                  </div>
                  
                  <div className="bg-[#FAFAFB] border border-[#0F172A05] rounded-2xl p-6 transition-all hover:shadow-xs hover:border-indigo-500/20">
                    <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full uppercase tracking-wider mb-2 inline-block">
                      {item.year}
                    </span>
                    <h4 className="text-base font-black text-[#111111] mt-1">{item.title}</h4>
                    <p className="text-sm text-slate-500 font-medium mt-2 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ABA 3: PERFORMANCE */}
        {activeTab === 'performance' && (
          <div className="flex flex-col gap-8">
            
            {/* Cards de Métricas Detalhadas de Produtividade */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white border border-[#0F172A0D] p-6 rounded-3xl flex flex-col gap-1 shadow-xs">
                <span className="text-[10px] font-black text-[#64748B] uppercase tracking-widest">Tarefas Concluídas</span>
                <h3 className="text-3xl font-black text-[#111111] mt-2">{memberTasks.filter((task: any) => ['DONE', 'COMPLETED', 'CONCLUIDO', 'Concluído'].includes(String(task.status || '').toUpperCase())).length}</h3>
                <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1 mt-1">
                  <TrendingUp size={12} /> Dados persistidos
                </span>
              </div>
              <div className="bg-white border border-[#0F172A0D] p-6 rounded-3xl flex flex-col gap-1 shadow-xs">
                <span className="text-[10px] font-black text-[#64748B] uppercase tracking-widest">Velocidade Média</span>
                <h3 className="text-3xl font-black text-[#111111] mt-2">Não informado</h3>
                <span className="text-xs font-semibold text-indigo-600 flex items-center gap-1 mt-1">
                  <Clock size={12} /> Sem histórico de duração
                </span>
              </div>
              <div className="bg-white border border-[#0F172A0D] p-6 rounded-3xl flex flex-col gap-1 shadow-xs">
                <span className="text-[10px] font-black text-[#64748B] uppercase tracking-widest">Taxa de Qualidade</span>
                <h3 className="text-3xl font-black text-[#111111] mt-2">Não informado</h3>
                <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1 mt-1">
                  <CheckCircle2 size={12} /> Sem avaliações registradas
                </span>
              </div>
              <div className="bg-white border border-[#0F172A0D] p-6 rounded-3xl flex flex-col gap-1 shadow-xs">
                <span className="text-[10px] font-black text-[#64748B] uppercase tracking-widest">Sprint Feedbacks</span>
                <h3 className="text-3xl font-black text-[#111111] mt-2">Não informado</h3>
                <span className="text-xs font-semibold text-amber-500 flex items-center gap-1 mt-1">
                  <Star size={12} fill="currentColor" /> Sem feedback registrado
                </span>
              </div>
            </div>

            {/* Performance charts area */}
            <div className="bg-white border border-[#0F172A0D] rounded-3xl p-6 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 pb-4 border-b border-[#0F172A05] gap-4">
                <div>
                  <h3 className="text-base font-black text-[#111111]">Produtividade & Qualidade por Sprint</h3>
                  <p className="text-xs text-[#64748B] font-medium">Histórico recente de entregas e assertividade técnica.</p>
                </div>
                <div className="flex items-center gap-4 text-xs font-bold">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-xs bg-indigo-600" /> Entregas</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-xs bg-emerald-500" /> Qualidade</span>
                </div>
              </div>

              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartPerformanceData}>
                    <defs>
                      <linearGradient id="colorEntregas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorQualidade" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0', padding: '10px' }} />
                    <Area type="monotone" dataKey="entregas" stroke="#4F46E5" fillOpacity={1} fill="url(#colorEntregas)" strokeWidth={2.5} />
                    <Area type="monotone" dataKey="qualidade" stroke="#10B981" fillOpacity={1} fill="url(#colorQualidade)" strokeWidth={2.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>
        )}

        {/* ABA 4: SKILLS */}
        {activeTab === 'skills' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Tecnologias & Hard Skills */}
            <div className="bg-white border border-[#0F172A0D] rounded-3xl p-6 shadow-xs">
              <h3 className="text-base font-black text-[#111111] mb-6 flex items-center gap-2 border-b border-[#0F172A05] pb-4">
                <Zap size={16} className="text-amber-500" /> Hard Skills & Tecnologias
              </h3>
              
              <div className="space-y-6">
                {profileDetails.skills.technologies.map((skill: any) => (
                  <div key={skill.name} className="flex flex-col gap-2">
                    <div className="flex justify-between items-center text-xs font-bold text-slate-800">
                      <span>{skill.name}</span>
                      <span className="text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md text-[10px]">{skill.level}% Proficiente</span>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full" style={{ width: `${skill.level}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Soft Skills */}
            <div className="bg-white border border-[#0F172A0D] rounded-3xl p-6 shadow-xs">
              <h3 className="text-base font-black text-[#111111] mb-6 flex items-center gap-2 border-b border-[#0F172A05] pb-4">
                <Users size={16} className="text-emerald-500" /> Soft Skills & Atitudes
              </h3>

              <div className="space-y-6">
                {profileDetails.skills.soft.map((skill: any) => (
                  <div key={skill.name} className="flex flex-col gap-2">
                    <div className="flex justify-between items-center text-xs font-bold text-slate-800">
                      <span>{skill.name}</span>
                      <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md text-[10px]">{skill.level}% Nível</span>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full" style={{ width: `${skill.level}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* ABA 5: PROJETOS */}
        {activeTab === 'projetos' && (
          <div className="bg-white border border-[#0F172A0D] rounded-3xl p-6 shadow-xs">
            <div className="border-b border-[#0F172A05] pb-4 mb-6">
              <h3 className="text-base font-black text-[#111111]">Alocação Estratégica em Projetos</h3>
              <p className="text-sm text-[#64748B] font-medium mt-1">Mapeamento de funções e impacto em cada iniciativa ativa na empresa.</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#0F172A0D]">
                    <th className="px-6 py-4 text-[10px] font-black text-[#64748B] uppercase tracking-widest">Iniciativa / Projeto</th>
                    <th className="px-6 py-4 text-[10px] font-black text-[#64748B] uppercase tracking-widest">Papel Principal</th>
                    <th className="px-6 py-4 text-[10px] font-black text-[#64748B] uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-[10px] font-black text-[#64748B] uppercase tracking-widest">Impacto Gerado</th>
                    <th className="px-6 py-4 text-[10px] font-black text-[#64748B] uppercase tracking-widest">Progresso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0F172A05]">
                  {linkedProjects.map((p: any) => (
                    <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-[#111111]">{p.name}</span>
                          <span className="text-xs text-[#64748B] mt-0.5">Prazo de Entrega: {new Date(p.dueDate).toLocaleDateString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold bg-[#FAFAFB] border border-[#0F172A08] text-slate-800 px-2.5 py-1 rounded-lg">
                          Backend Lead
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700">
                          {p.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold text-emerald-600">Alta Eficiência</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3 max-w-[120px]">
                          <div className="h-1.5 bg-[#FAFAFA] border border-[#0F172A0A] rounded-full overflow-hidden flex-1">
                            <div className="h-full bg-indigo-500 animate-pulse" style={{ width: `${p.progress || 72}%` }} />
                          </div>
                          <span className="text-xs font-bold text-[#111111]">{p.progress || 72}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {linkedProjects.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-xs text-[#64748B] font-medium">Nenhum projeto vinculado encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ABA 6: ATIVIDADES */}
        {activeTab === 'atividades' && (
          <div className="bg-white border border-[#0F172A0D] rounded-3xl p-6 shadow-xs">
            <div className="border-b border-[#0F172A05] pb-4 mb-6">
              <h3 className="text-base font-black text-[#111111]">Linha do Tempo de Atividades</h3>
              <p className="text-sm text-[#64748B] font-medium mt-1">Histórico completo de interações operacionais e conclusão de sprints.</p>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50/50 border border-[#0F172A05]">
                <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0 mt-0.5">
                  <CheckCircle2 size={15} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Hoje</h4>
                  <p className="text-sm font-bold text-[#111111] mt-1">Criou documentação técnica de infraestrutura</p>
                  <p className="text-xs text-slate-500 mt-1">Estruturou o guia de migração de APIs REST para GraphQL de forma impecável.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50/50 border border-[#0F172A05]">
                <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0 mt-0.5">
                  <Briefcase size={15} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Ontem</h4>
                  <p className="text-sm font-bold text-[#111111] mt-1">Finalizou tarefa estratégica de criptografia</p>
                  <p className="text-xs text-slate-500 mt-1">Concluiu e testou os payloads de segurança das chaves na AWS KMS.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50/50 border border-[#0F172A05]">
                <div className="w-8 h-8 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 mt-0.5">
                  <Award size={15} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Semana passada</h4>
                  <p className="text-sm font-bold text-[#111111] mt-1">Participou da revisão geral de arquitetura</p>
                  <p className="text-xs text-slate-500 mt-1">Apresentou a nova modelagem de banco de dados postgres para toda a equipe.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ABA 7: FINANCEIRO */}
        {activeTab === 'financeiro' && (
          <div className="flex flex-col gap-8 animate-fade-in">
            
            {/* Finance dashboard cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-[#0F172A0D] p-6 rounded-3xl flex flex-col gap-1 shadow-xs">
                <span className="text-[10px] font-black text-[#64748B] uppercase tracking-widest">Tipo de Contrato</span>
                <h3 className="text-xl font-black text-slate-900 mt-2">{profileDetails.financial.contractType}</h3>
                <span className="text-xs font-semibold text-indigo-600 mt-1">Fidelidade corporativa</span>
              </div>
              <div className="bg-white border border-[#0F172A0D] p-6 rounded-3xl flex flex-col gap-1 shadow-xs">
                <span className="text-[10px] font-black text-[#64748B] uppercase tracking-widest">Custo Mensal Corporativo</span>
                <h3 className="text-2xl font-black text-slate-900 mt-2">R$ {profileDetails.financial.monthlyCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                <span className="text-xs font-semibold text-slate-500 mt-1">Custo direto consolidado</span>
              </div>
              <div className="bg-white border border-[#0F172A0D] p-6 rounded-3xl flex flex-col gap-1 shadow-xs">
                <span className="text-[10px] font-black text-[#64748B] uppercase tracking-widest">Valor Financeiro Gerado (Estimado)</span>
                <h3 className="text-2xl font-black text-emerald-600 mt-2">R$ {profileDetails.financial.generatedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 mt-1">
                  <TrendingUp size={12} /> ROI Excepcional de Contribuição
                </span>
              </div>
            </div>

            {/* Strategic investment analysis & history */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Investment breakdown */}
              <div className="bg-white border border-[#0F172A0D] rounded-3xl p-6 shadow-xs flex flex-col gap-4">
                <h3 className="text-base font-black text-[#111111] border-b border-[#0F172A05] pb-3">Investimento na Pessoa</h3>
                <div className="flex justify-between items-center py-2.5 border-b border-dashed border-slate-100 text-sm">
                  <span className="font-semibold text-slate-500">Capacitações & Cursos Pagos</span>
                  <span className="font-bold text-[#111111]">R$ 4.500,00</span>
                </div>
                <div className="flex justify-between items-center py-2.5 border-b border-dashed border-slate-100 text-sm">
                  <span className="font-semibold text-slate-500">Equipamento de Ponta Fornecido</span>
                  <span className="font-bold text-[#111111]">R$ 12.000,00</span>
                </div>
                <div className="flex justify-between items-center py-2.5 border-b border-dashed border-slate-100 text-sm">
                  <span className="font-semibold text-slate-500">Certificações de Nuvem</span>
                  <span className="font-bold text-[#111111]">R$ 3.000,00</span>
                </div>
                <div className="flex justify-between items-center py-2.5 text-sm font-black">
                  <span className="text-[#111111]">Total Investido</span>
                  <span className="text-indigo-600">R$ {profileDetails.financial.investment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Salary / contract History */}
              <div className="bg-white border border-[#0F172A0D] rounded-3xl p-6 shadow-xs flex flex-col gap-4">
                <h3 className="text-base font-black text-[#111111] border-b border-[#0F172A05] pb-3">Histórico Financeiro & Mérito</h3>
                <div className="space-y-4">
                  {profileDetails.financial.history.map((h: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center p-3 rounded-2xl bg-[#FAFAFB] border border-[#0F172A05]">
                      <div>
                        <span className="text-xs font-semibold text-slate-400 block">{h.date}</span>
                        <span className="text-xs font-bold text-slate-800">{h.event}</span>
                      </div>
                      <span className="text-sm font-black text-slate-900 dark:text-white">{h.value}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

          </div>
        )}

        {/* ABA 8: CONQUISTAS */}
        {activeTab === 'conquistas' && (
          <div className="bg-white dark:bg-[#0D1220] border border-[#0F172A0D] dark:border-white/5 rounded-3xl p-8 shadow-xs">
            <div className="border-b border-[#0F172A05] dark:border-white/5 pb-4 mb-8">
              <h3 className="text-base font-black text-[#111111] dark:text-white">Badges de Conquistas (Achievements)</h3>
              <p className="text-sm text-[#64748B] dark:text-slate-400 font-medium mt-1">Badges gamificados que reconhecem feitos técnicos, velocidade de sprint e dedicação cultural.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {profileDetails.achievements.map((ach: any) => (
                <div key={ach.id} className="bg-[#FAFAFB] dark:bg-[#151D30] border border-[#0F172A0A] dark:border-white/5 rounded-2xl p-6 flex flex-col items-center text-center relative group hover:border-indigo-500/20 transition-all hover:scale-105">
                  <div className="text-4xl mb-4 p-4 rounded-full bg-white dark:bg-[#0D1220] shadow-sm transform group-hover:rotate-12 transition-transform">
                    {ach.icon}
                  </div>
                  <h4 className="text-sm font-black text-[#111111] dark:text-white">{ach.title}</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-2 leading-relaxed">{ach.desc}</p>
                  <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 mt-4 block bg-indigo-50 dark:bg-indigo-950/40 px-3 py-1 rounded-full uppercase tracking-wider">{ach.date}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ABA 9: IA COACH */}
        {activeTab === 'ia-coach' && (
          <div className="bg-gradient-to-br from-indigo-950 to-[#0A0D1A] text-white rounded-3xl p-8 shadow-md relative overflow-hidden">
            
            {/* Glowing neon sphere decorator */}
            <div className="absolute -top-24 -right-24 w-80 h-80 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-[-100px] left-[-100px] w-64 h-64 bg-violet-600/15 rounded-full blur-3xl pointer-events-none" />

            <div className="flex items-center gap-3 border-b border-white/10 pb-6 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-400/20 flex items-center justify-center text-indigo-400">
                <Sparkles size={24} className="animate-pulse" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight flex items-center gap-2">
                  Cyzor Neural Coach <span className="bg-indigo-500 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-full">Preview</span>
                </h3>
                <p className="text-xs text-slate-400 font-medium italic mt-0.5">Análise e sugestões de PDI automáticas baseadas em comportamento de entregas.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              {/* Pontos fortes */}
              <div className="bg-white/5 border border-white/5 rounded-2xl p-6">
                <h4 className="text-xs font-black text-emerald-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                  <CheckCircle2 size={14} /> Pontos Fortes
                </h4>
                <ul className="space-y-3 text-sm font-semibold text-slate-200">
                  {profileDetails.coach.strongPoints.map((pt: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">•</span>
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Oportunidades */}
              <div className="bg-white/5 border border-white/5 rounded-2xl p-6">
                <h4 className="text-xs font-black text-amber-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                  <TrendingUp size={14} /> Oportunidades
                </h4>
                <ul className="space-y-3 text-sm font-semibold text-slate-200">
                  {profileDetails.coach.opportunities.map((op: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-amber-400 mt-1">•</span>
                      <span>{op}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Sugestão estratégica */}
              <div className="bg-white/5 border border-white/5 rounded-2xl p-6 flex flex-col justify-between">
                <div>
                  <h4 className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                    <Sparkles size={14} /> Sugestão da IA
                  </h4>
                  <p className="text-sm font-medium text-slate-200 leading-relaxed italic">
                    "{profileDetails.coach.suggestion}"
                  </p>
                </div>
                
                <button 
                  onClick={() => alert('Plano de Desenvolvimento Individual (PDI) gerado na workspace com sucesso!')}
                  className="w-full mt-6 bg-indigo-600 hover:bg-indigo-500 text-white py-3.5 px-4 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  <Sparkles size={13} /> Gerar Plano de Ação
                </button>
              </div>

            </div>

            {/* Neural analysis feedback footer */}
            <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-4">
              <span>Última recalibração neural: Hoje às 08:42</span>
              <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Cyzor Control v2.5 Engine</span>
            </div>

          </div>
        )}

      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          MODAL DE EDIÇÃO DE NOME (PROTOTIPADO)
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {showEditNameModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4" id="edit-name-modal-container">
          <div className="bg-white dark:bg-[#0D1220] border border-[#0F172A0D] dark:border-white/5 rounded-3xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-black text-[#111111] dark:text-white mb-2">Editar Nome do Colaborador</h3>
            <p className="text-xs text-[#64748B] dark:text-slate-400 font-medium mb-6">Insira o nome corporativo e profissional do colaborador.</p>
            
            <input 
              type="text"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              placeholder="Nome completo..."
              className="w-full bg-[#FAFAFB] dark:bg-[#151D30] border border-[#0F172A0A] dark:border-white/5 text-[#111111] dark:text-slate-200 rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-black/5 outline-none mb-6"
            />

            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setShowEditNameModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1E293B] cursor-pointer"
              >
                Cancelar
              </button>
              <button 
                onClick={handleUpdateName}
                className="bg-black dark:bg-indigo-600 text-white px-5 py-2 rounded-xl text-xs font-bold hover:bg-neutral-800 dark:hover:bg-indigo-500 cursor-pointer shadow-sm"
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
